import { withRequestTimeout } from './timeout';
import { isAuthStatus, SearchAuthError } from './types';

/** How long a host that failed is skipped. */
export const HOST_DOWN_MS = 2 * 60_000;

/** Host → the time it may be tried again after a network error, a timeout or a 5xx. */
const downUntil = new Map<string, number>();

/** Forgets every host failure. */
export const forgetHostFailures = (): void => downUntil.clear();

/** The hosts not marked down, in order; every host when all of them are. */
const hostsToTry = (hosts: readonly string[]): readonly string[] => {
  const now = Date.now();
  const up = hosts.filter((host) => (downUntil.get(host) ?? 0) <= now);
  return up.length > 0 ? up : hosts;
};

export interface FailoverRequest {
  readonly url: URL | string;
  readonly init: Omit<RequestInit, 'signal'>;
}

/**
 * POSTs the request each host is given until one answers. Each try has its own timeout, which covers
 * the body read as well; a network error, a timeout, a 5xx or an unreadable body marks the host down
 * for HOST_DOWN_MS and moves on, unless the caller's own signal aborted. 401/403 throws
 * SearchAuthError and any other non-2xx throws at once; the last error is thrown when every host
 * failed. `label` names the engine in the error messages.
 */
export async function postWithFailover(
  hosts: readonly string[],
  requestFor: (host: string) => FailoverRequest,
  signal: AbortSignal | undefined,
  label: string
): Promise<unknown> {
  let lastError: unknown = new Error(`${label} search failed`);
  for (const host of hostsToTry(hosts)) {
    const { url, init } = requestFor(host);
    let response: Response;
    try {
      response = await fetch(url, { ...init, signal: withRequestTimeout(signal) });
    } catch (error) {
      if (signal?.aborted) throw error;
      downUntil.set(host, Date.now() + HOST_DOWN_MS);
      lastError = error;
      continue;
    }

    if (isAuthStatus(response.status)) throw new SearchAuthError(response.status);
    if (response.status >= 500) {
      downUntil.set(host, Date.now() + HOST_DOWN_MS);
      lastError = new Error(`${label} responded ${response.status}`);
      await response.body?.cancel().catch(() => undefined);
      continue;
    }
    if (!response.ok) throw new Error(`${label} responded ${response.status}`);
    try {
      const body: unknown = await response.json();
      downUntil.delete(host);
      return body;
    } catch (error) {
      // Headers arrived but the body stalled past the timeout, or is not JSON: the host's failure, not the caller's.
      if (signal?.aborted) throw error;
      downUntil.set(host, Date.now() + HOST_DOWN_MS);
      lastError = error;
    }
  }

  throw lastError;
}
